// ** Modules
import React from 'react';
import axios from 'axios';
import Image from 'next/image';

// ** UI
import Box from '@mui/system/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// ** Icons
import ImageIcon from '@mui/icons-material/Image';
import ArchiveIcon from '@mui/icons-material/Archive';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

// ** Components
import Link from '@/components/common/Link';

// ** Utils
import { getStrapiFileUrl } from '@/utils/api/strapi';

// ** Types
import type { AccordionSubComponentProps } from './index';

// Default fallback icons
const fallbackIcons = {
	pdf: PictureAsPdfIcon,
	image: ImageIcon,
	doc: InsertDriveFileIcon,
	archive: ArchiveIcon,
	audio: AudiotrackIcon,
	video: VideoLibraryIcon,
	default: InsertDriveFileIcon
};

const DownloadItemsComponent = ({
	host,
	isExpanded,
	currentUser,
	accordionElementTypeFiles = [],
	accordionElementFilesAuthenticated
}: AccordionSubComponentProps) => {
	const [thumbnails, setThumbnails] = React.useState<Record<string, any>>({});

	const accordionElementFiles = React.useMemo(() => {
		return accordionElementTypeFiles?.flatMap((typeFile: any) => typeFile?.accordionElementFiles || []) || [];
	}, [accordionElementTypeFiles]);

	// --- Thumbnail Logic ---
	const generateThumbnailFromApi = React.useCallback(
		async ({ name, fileUrl, mime }: any) => {
			const supportedMimeTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/gif'];
			if (!supportedMimeTypes.includes(mime.toLowerCase())) {
				setThumbnails((prev) => ({ ...prev, [name]: fallbackIcons.default }));
				return;
			}

			const fullUrl = getStrapiFileUrl({ host, url: fileUrl }).replace(/([^:]\/)\/+/g, '$1');

			try {
				const response = await axios.get(
					`/api/generate-thumbnail?fileUrl=${encodeURIComponent(fullUrl)}&fileName=${encodeURIComponent(name)}&mime=${encodeURIComponent(mime)}`,
					{ responseType: 'json' }
				);
				let thumbnailUrl = response.data.image;
				if (!thumbnailUrl.includes('.1.png')) thumbnailUrl = thumbnailUrl.replace(/(\.png)$/, '.1$1');

				localStorage.setItem(`thumbnail-${name}`, thumbnailUrl);
				setThumbnails((prev) => ({ ...prev, [name]: thumbnailUrl }));
			} catch (error) {
				console.error(error);
				setThumbnails((prev) => ({ ...prev, [name]: fallbackIcons.default }));
			}
		},
		[host]
	);

	const checkFileExistence = React.useCallback(async (fileUrl: string) => {
		try {
			const response = await fetch(fileUrl, { method: 'HEAD' });
			return response.ok;
		} catch {
			return false;
		}
	}, []);

	const generateThumbnail = React.useCallback(
		async (fileItem: any) => {
			const fileData = fileItem?.file;
			if (!fileData) return;
			const { url, name, mime } = fileData;

			// 1. Check local storage cache using the NEW endpoint scheme as the key
			const cacheKey = `thumbnail-${name}`;
			const cachedThumbnail = localStorage.getItem(cacheKey);

			if (cachedThumbnail) {
				if (await checkFileExistence(cachedThumbnail)) {
					setThumbnails((prev) => ({ ...prev, [name]: cachedThumbnail }));
					return;
				}
				localStorage.removeItem(cacheKey);
			}

			// 2. FORCE Next.js to request the binary via the API instead of a static route folder
			const thumbnailUrl = `/api/generate-thumbnail?fallback=true&fileName=${encodeURIComponent(name)}`;

			try {
				const response = await fetch(thumbnailUrl, { method: 'HEAD' });
				if (response.ok) {
					localStorage.setItem(cacheKey, thumbnailUrl);
					setThumbnails((prev) => ({ ...prev, [name]: thumbnailUrl }));
					return;
				}
			} catch {
				console.error('Error fetching thumbnail via API route stream');
			}

			// 3. Fallback to full generation if the asset doesn't exist yet
			await generateThumbnailFromApi({ name, fileUrl: url, mime });
		},
		[generateThumbnailFromApi, checkFileExistence]
	);

	// Only generate thumbnails when the section is expanded
	React.useEffect(() => {
		if (isExpanded && accordionElementFiles.length > 0) {
			accordionElementFiles.forEach((file: any) => {
				generateThumbnail(file);
			});
		}
	}, [isExpanded, accordionElementFiles, generateThumbnail]);

	const handleDownload = async (fileUrl: string) => {
		try {
			const response = await axios.get(fileUrl.trim(), {
				responseType: 'blob'
			});
			const blob = new Blob([response.data]);
			const rawFileName =
				fileUrl
					.trim()
					.split('/')
					.pop()
					?.replace(/(\.\w+)[\s_]*$/, '$1') || 'download';

			const downloadUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = downloadUrl;
			link.setAttribute('download', rawFileName);
			document.body.appendChild(link);
			link.click();
			link.remove();
		} catch (error) {
			console.error('Download failed', error);
		}
	};

	return (
		<Grid container spacing={6} sx={{ pb: 6, pt: 2 }}>
			{accordionElementFiles.map((item: any) => {
				const name = item?.file?.name ?? '';
				const url = item?.file?.url ?? '';
				const displayName = name.replace(/_/g, ' ');
				const description = item?.fileDescription;
				const ThumbnailContent = thumbnails[name];

				return (
					<Grid key={name} size={{ xs: 12, md: 6, lg: 4 }}>
						<Stack direction="row" spacing={3} sx={{ alignItems: 'center' }}>
							<Box
								sx={{
									width: 80,
									height: 100,
									bgcolor: 'action.hover',
									position: 'relative',
									flexShrink: 0,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center'
								}}
							>
								{typeof ThumbnailContent === 'string' ? (
									<Image src={ThumbnailContent} alt={name} layout="fill" objectFit="cover" />
								) : (
									<InsertDriveFileIcon sx={{ fontSize: '2.5rem', opacity: 0.3 }} />
								)}
							</Box>

							<Stack spacing={0.5}>
								<Typography variant="caption" noWrap sx={{ maxWidth: 200, fontWeight: 700 }}>
									{displayName}
								</Typography>

								{description && (
									<Typography variant="caption" sx={{ opacity: 0.7 }}>
										{description}
									</Typography>
								)}

								{!accordionElementFilesAuthenticated || currentUser ? (
									<Typography
										variant="caption"
										color="primary"
										onClick={() => handleDownload(getStrapiFileUrl({ host, url }))}
										sx={{
											fontWeight: 700,
											cursor: 'pointer',
											'& :hover': { textDecoration: 'underline' }
										}}
									>
										Download
									</Typography>
								) : (
									<Link href="/login">
										<Typography variant="caption" color="primary" sx={{ fontWeight: 700 }}>
											Zum Download Anmelden
										</Typography>
									</Link>
								)}
							</Stack>
						</Stack>
					</Grid>
				);
			})}
		</Grid>
	);
};

export default DownloadItemsComponent;
